import type { ActiveBundlesResponse } from '@features/subscriptions/types';
import type { GetUserInfoResponse, UserAccessInfo } from '@features/user/store/types';
import type { StatsigUser } from '@jotpsych/statsig';
import { UTMTracker } from '@services/utm';
import { parseUTCDate } from '@utils/dateUtils';

/**
 * Statsig identities are compared by serialized content (see
 * `useStatsigUserSync`), so set-like arrays must serialize the same however
 * the API ordered them. The backend builds `roles` from an unordered set, so
 * consecutive user-info responses can return the same roles in a different
 * order; without this, every such response re-synced the same identity.
 */
function sorted(values: readonly string[] | undefined): string[] | undefined {
  return values ? [...values].sort() : undefined;
}

/** One targeting shape for the authenticated app and the public knowledge base. */
export function buildStatsigUser(
  userState:
    | (Omit<GetUserInfoResponse, 'organization_info'> & Pick<UserAccessInfo, 'organization_info'>)
    | null
    | undefined,
  bundles: ActiveBundlesResponse | undefined,
  email?: string,
): StatsigUser | null {
  if (!userState?.user_id) return null;
  const userBundles = {
    tier: bundles?.bundles?.find(b => b.bundle_type === 'tier')?.bundle_id ?? null,
    addons:
      sorted(bundles?.bundles?.filter(b => b.bundle_type === 'add_on').map(b => b.bundle_id)) ?? [],
  };
  const currentUTMs = UTMTracker.getUTMs();
  return {
    userID: userState.user_id,
    customIDs: {
      organizationID: userState.organization_id ?? undefined,
    },
    email: userState.user_info?.email || email,
    custom: {
      // Also present as customIDs.organizationID; duplicated here because
      // the event backbone's statsig webhook reads the tenant from the
      // delivered user object to stamp organization_id on v2 envelopes
      // (GDN-2622), and `custom` is the contract it relies on.
      organization_id: userState.organization_id ?? undefined,

      specialization: userState.user_info?.specialization,
      roles: sorted(userState.roles),

      paymentStatus: userState.payment_status,
      emailVerified: userState.email_verified,

      // TODO: remove once we migrate Statsig to use organizationID.
      groupIds: sorted(userState.group_ids),

      organization_size: userState.organization_info?.size,
      organization_group: userState.organization_info?.group,

      patientMatchEnrolled: userState.patient_match_enrolled,
      clearinghouseLinked: userState.clearinghouse_linked,

      created_at: userState.created_at ?? undefined,
      is_new_user: !userState.onboarding_steps?.account_creation_onboarding_completed,
      signup_timestamp: userState.created_at
        ? Math.floor(parseUTCDate(userState.created_at).getTime() / 1000)
        : undefined,
      days_since_signup: userState.created_at
        ? Math.floor(
            (Date.now() - parseUTCDate(userState.created_at).getTime()) / (1000 * 60 * 60 * 24),
          )
        : undefined,

      ...currentUTMs,

      ...userState.feature_flags,

      subscription_tier: userBundles.tier ?? undefined,
      subscription_addons: userBundles.addons,
    },
  };
}
